import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridColumnGroup } from "@mui/x-data-grid-premium";
import { Link } from "react-router";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { Fetching } from "components/common/loading/fetching";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";
import { buildAccuracyPageUrl } from "components/labellerAccuracy/utils";
import { isFetchBaseQueryError } from "helpers";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMaterials } from "hooks/useMaterials";
import { useFormatNumber } from "src/utils/formatNumber";
import { useCallback, useMemo, useState } from "react";
import {
  LabellerStatsEntry,
  useGetLabellerCostStatsQuery,
} from "src/store/api/generatedApi";
import { AccuracyTooltip, PinnedTooltipProvider } from "./AccuracyTooltip";
import { DateRangeFilterBar, useDateRangeFilters } from "./dateRangeFilterBar";
import { CsvColumnDef } from "src/utils/csv";
import { CopyWorkerStatsToClipboardButton } from "./copyStatsToClipboardButton";
import {
  LabellerStatusFilterChips,
  matchesLabellerStatusFilter,
  useLabellerStatusFilters,
} from "./labellerStatusFilters";
import {
  buildLabellerBaseColumns,
  buildLabellerBaseCsvColumns,
  LABELLER_NAME_FIELD,
} from "./labellerOverviewColumns";
import {
  falseNegativeRate,
  falseNegativeRateCsv,
  falsePositiveRate,
  falsePositiveRateCsv,
} from "./labellerAccuracyRates";

type LabellerRow = LabellerStatsEntry & { id: string };

const formatPercent = (
  value: number | null | undefined,
  formatter: (value: number) => string
): string => (value != null ? `${formatter(value * 100)}%` : "\u2014");

export const LabellerOverviewTab = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const formatNumber1dp = useFormatNumber()(1);
  const dateFilters = useDateRangeFilters();
  const [location, setLocation] = useState("");
  const statusFilters = useLabellerStatusFilters();

  const { locationNames } = useLabellingLocations();
  const drilldownLocationFor = useCallback(
    (row: LabellerRow) => {
      if (location !== "") return location;
      return (row.accuracy?.location_names ?? [])[0] ?? locationNames[0] ?? "";
    },
    [location, locationNames]
  );

  const { materials } = useMaterials();
  const formatMaterialLabel = useMemo(() => {
    const nameToLabellingName = new Map(
      (materials ?? []).map((material) => [
        material.name,
        material.labelling_name ?? material.name,
      ])
    );
    return (name: string) => nameToLabellingName.get(name) ?? name;
  }, [materials]);

  const { data, isLoading, isFetching, error } = useGetLabellerCostStatsQuery({
    tenantName,
    start: dateFilters.start?.toISOString() ?? undefined,
    end: dateFilters.end?.toISOString() ?? undefined,
    filterMode: dateFilters.filterMode,
    location: location || undefined,
  });

  const rows = useMemo(
    () =>
      (data?.labeller_stats ?? []).map((entry): LabellerRow => ({
        ...entry,
        id: entry.labeller.id,
      })),
    [data]
  );

  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        matchesLabellerStatusFilter(
          statusFilters,
          row.labeller.labeller_type,
          row.labeller.model_status
        )
      ),
    [rows, statusFilters]
  );

  const columns = useMemo(
    (): GridColDef<LabellerRow>[] => [
      ...buildLabellerBaseColumns(t, formatNumber1dp),
      {
        field: "match_accuracy",
        headerName: t("matchAccuracy"),
        type: "number",
        width: 130,
        valueGetter: (_value, row) => row.accuracy?.match_accuracy,
        valueFormatter: (value?: number | null) =>
          formatPercent(value, formatNumber1dp),
        renderCell: ({ row, value }) => (
          <AccuracyTooltip
            type="match"
            matchedEvents={row.accuracy?.matched_events ?? 0}
            totalLabellerEvents={row.accuracy?.total_labeller_events ?? 0}
            totalVerifiedEvents={row.accuracy?.total_verified_events ?? 0}
          >
            {formatPercent(value as number | null | undefined, formatNumber1dp)}
          </AccuracyTooltip>
        ),
      },
      {
        field: "false_positive_rate",
        headerName: t("falsePositiveRate"),
        type: "number",
        width: 120,
        valueGetter: (_value, row) => falsePositiveRate(row.accuracy),
        valueFormatter: (value?: number | null) =>
          formatPercent(value, formatNumber1dp),
      },
      {
        field: "false_negative_rate",
        headerName: t("falseNegativeRate"),
        type: "number",
        width: 120,
        valueGetter: (_value, row) => falseNegativeRate(row.accuracy),
        valueFormatter: (value?: number | null) =>
          formatPercent(value, formatNumber1dp),
      },
      {
        field: "area_accuracy",
        headerName: t("areaAccuracy"),
        type: "number",
        width: 130,
        valueGetter: (_value, row) => row.accuracy?.area_accuracy,
        valueFormatter: (value?: number | null) =>
          formatPercent(value, formatNumber1dp),
        renderCell: ({ row, value }) => (
          <AccuracyTooltip
            type="confusion_matrix"
            entries={row.accuracy?.area_confusion_matrix ?? []}
            formatLabel={formatArea}
            buildCellHref={(predicted, actual) =>
              buildAccuracyPageUrl({
                tenantName,
                labellerId: row.labeller.id,
                metric: "area",
                predicted,
                actual,
                location: drilldownLocationFor(row),
                start: dateFilters.start,
                end: dateFilters.end,
              })
            }
          >
            {formatPercent(value as number | null | undefined, formatNumber1dp)}
          </AccuracyTooltip>
        ),
      },
      {
        field: "material_accuracy",
        headerName: t("materialAccuracy"),
        type: "number",
        width: 140,
        valueGetter: (_value, row) => row.accuracy?.material_accuracy,
        valueFormatter: (value?: number | null) =>
          formatPercent(value, formatNumber1dp),
        renderCell: ({ row, value }) => (
          <AccuracyTooltip
            type="confusion_matrix"
            entries={row.accuracy?.material_confusion_matrix ?? []}
            formatLabel={formatMaterialLabel}
            buildCellHref={(predicted, actual) =>
              buildAccuracyPageUrl({
                tenantName,
                labellerId: row.labeller.id,
                metric: "material",
                predicted,
                actual,
                location: drilldownLocationFor(row),
                start: dateFilters.start,
                end: dateFilters.end,
              })
            }
          >
            {formatPercent(value as number | null | undefined, formatNumber1dp)}
          </AccuracyTooltip>
        ),
      },
      {
        field: "timing_accuracy",
        headerName: t("timingAccuracy"),
        type: "number",
        width: 130,
        valueGetter: (_value, row) => row.accuracy?.average_timing_iou,
        valueFormatter: (value?: number | null) =>
          formatPercent(value, formatNumber1dp),
        renderCell: ({ row, value }) => {
          const formatted = formatPercent(
            value as number | null | undefined,
            formatNumber1dp
          );
          if (row.accuracy?.average_timing_iou == null) {
            return formatted;
          }
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "100%",
                gap: 0.5,
              }}
            >
              {formatted}
              <Tooltip title={t("openTimingDrilldown")}>
                <Box
                  component={Link}
                  to={buildAccuracyPageUrl({
                    tenantName,
                    labellerId: row.labeller.id,
                    metric: "timing",
                    location: drilldownLocationFor(row),
                    start: dateFilters.start,
                    end: dateFilters.end,
                  })}
                  aria-label={t("openTimingDrilldown")}
                  sx={{
                    display: "inline-flex",
                    color: "action.disabled",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      t,
      formatNumber1dp,
      formatMaterialLabel,
      tenantName,
      drilldownLocationFor,
      dateFilters.start,
      dateFilters.end,
    ]
  );

  const columnGroupingModel = useMemo(
    (): GridColumnGroup[] => [
      {
        groupId: "matchDetection",
        headerName: t("matchDetection"),
        headerClassName: "centered-group-header",
        children: [
          { field: "match_accuracy" },
          { field: "false_positive_rate" },
          { field: "false_negative_rate" },
        ],
      },
      {
        groupId: "conditionalOnMatch",
        headerName: t("conditionalOnMatch"),
        headerClassName: "centered-group-header",
        children: [
          { field: "area_accuracy" },
          { field: "material_accuracy" },
          { field: "timing_accuracy" },
        ],
      },
    ],
    [t]
  );

  const csvColumns = useMemo(
    (): CsvColumnDef<LabellerRow>[] => [
      ...buildLabellerBaseCsvColumns(t),
      {
        header: t("matchAccuracy"),
        value: (row) => row.accuracy?.match_accuracy ?? null,
      },
      {
        header: t("falsePositiveRate"),
        value: (row) => falsePositiveRateCsv(row.accuracy),
      },
      {
        header: t("falseNegativeRate"),
        value: (row) => falseNegativeRateCsv(row.accuracy),
      },
      {
        header: t("areaAccuracy"),
        value: (row) => row.accuracy?.area_accuracy ?? null,
      },
      {
        header: t("materialAccuracy"),
        value: (row) => row.accuracy?.material_accuracy ?? null,
      },
      {
        header: t("timingAccuracy"),
        value: (row) => row.accuracy?.average_timing_iou ?? null,
      },
    ],
    [t]
  );

  if (error) {
    const detail = isFetchBaseQueryError(error)
      ? typeof error.data === "string"
        ? error.data
        : JSON.stringify(error.data)
      : (error as { message?: string }).message;

    return (
      <Alert severity="error">
        {t("unknownError")}
        {detail ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {detail}
          </Typography>
        ) : null}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ flex: 1, overflow: "hidden" }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
        <DateRangeFilterBar filters={dateFilters} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="labeller-location-filter-label">
            {t("location")}
          </InputLabel>
          <Select
            labelId="labeller-location-filter-label"
            value={location}
            label={t("location")}
            onChange={(e) => {
              setLocation(e.target.value);
            }}
          >
            <MenuItem value="">{t("all")}</MenuItem>
            {locationNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <LabellerStatusFilterChips filters={statusFilters} />
        <CopyWorkerStatsToClipboardButton
          rows={filteredRows}
          columns={csvColumns}
        />
      </Stack>
      <Fetching fetching={isFetching} sx={{ flex: 1, overflow: "hidden" }}>
        <PinnedTooltipProvider>
          <HotkeyCompliantDataGrid
            columns={columns}
            rows={filteredRows}
            columnGroupingModel={columnGroupingModel}
            loading={isLoading}
            initialState={{
              pinnedColumns: { left: [LABELLER_NAME_FIELD] },
              sorting: {
                sortModel: [{ field: LABELLER_NAME_FIELD, sort: "asc" }],
              },
            }}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            rowHeight={40}
            columnHeaderHeight={44}
            disableRowSelectionOnClick
            sx={{
              [".centered-group-header .MuiDataGrid-columnHeaderTitleContainer"]:
                {
                  justifyContent: "center",
                },
            }}
          />
        </PinnedTooltipProvider>
      </Fetching>
    </Stack>
  );
};
